import { useState, useEffect } from 'react';
import { enrollmentsApi, modulesApi, progressApi, facilitadoresApi, type ProgramEnrollment, type ProgramModule } from '../services/programService';
import { leadsService } from '@/services/leads';
import type { Lead } from '@/mocks/admin';

const STATUS_OPTIONS = [
  { value: 'diagnostico', label: 'Diagnóstico', color: 'bg-blue-100 text-blue-700' },
  { value: 'modulos', label: 'Módulos', color: 'bg-amber-100 text-amber-700' },
  { value: 'concluido', label: 'Concluído', color: 'bg-green-100 text-green-700' },
  { value: 'pausado', label: 'Pausado', color: 'bg-gray-100 text-gray-600' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-600' },
];

export default function EnrollmentsPage() {
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [modules, setModules] = useState<ProgramModule[]>([]);
  const [facilitadores, setFacilitadores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [form, setForm] = useState({ lead_id: '', facilitador_id: '', status: 'diagnostico', data_inicio: '', data_fim_prevista: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [ens, mods, leadsData, facs] = await Promise.all([
        enrollmentsApi.list(),
        modulesApi.list(),
        leadsService.getAll(),
        facilitadoresApi.list(),
      ]);
      setEnrollments(ens);
      setModules(mods);
      setLeads(leadsData);
      setFacilitadores(facs);
    } finally { setLoading(false); }
  };

  const openNew = (lead: Lead) => {
    setSelectedLead(lead);
    setForm({ lead_id: lead.id, facilitador_id: '', status: 'diagnostico', data_inicio: '', data_fim_prevista: '' });
    setShow(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const enrollment = await enrollmentsApi.create(form as any);
      for (const mod of modules) {
        await progressApi.upsert({ enrollment_id: enrollment.id, module_id: mod.id, progresso: 0, status: 'nao_iniciado', facilitador_id: form.facilitador_id || '' });
      }
      setShow(false);
      setSelectedLead(null);
      load();
    } finally { setSaving(false); }
  };

  const getStatusCfg = (s: string) => STATUS_OPTIONS.find(x => x.value === s) || STATUS_OPTIONS[0];

  // Mapa de lead_id -> enrollment para rápida consulta
  const enrollmentByLead = new Map(enrollments.map(en => [en.lead_id, en]));

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-black text-gray-900">Inscrições TR60D</h1>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : (
        <div className="space-y-3">
          {leads.map(lead => {
            const en = enrollmentByLead.get(lead.id);
            return (
              <div key={lead.id} className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {lead.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{lead.nome}</h3>
                      <p className="text-sm text-gray-500 truncate">{lead.email}</p>
                    </div>
                  </div>
                  {en ? (
                    // Cliente inscrito — mostra status dropdown
                    <div className="flex items-center gap-3">
                      <select
                        value={en.status}
                        onChange={async e => {
                          await enrollmentsApi.update(en.id, { status: e.target.value });
                          load();
                        }}
                        className="text-xs font-bold px-3 py-1.5 rounded-xl border border-gray-200 bg-white cursor-pointer"
                      >
                        {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      {en.progresso_total > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${en.progresso_total}%` }} />
                          </div>
                          <span className="text-xs text-gray-500">{en.progresso_total}%</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Cliente não inscrito — botão Inscrever
                    <button
                      onClick={() => openNew(lead)}
                      className="bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold text-xs px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      + Inscrever
                    </button>
                  )}
                </div>
                {en && (
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    {en.data_inicio && <span>Início: {en.data_inicio}</span>}
                    {en.data_fim_prevista && <span>Previsão: {en.data_fim_prevista}</span>}
                    {(en as any).facilitadores?.nome && <span>Consultor: {(en as any).facilitadores?.nome}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: criar inscrição */}
      {show && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-black text-gray-900 mb-4">
              Nova Inscrição — {selectedLead.nome}
            </h2>
            <form onSubmit={handleSave} className="space-y-3">
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="font-semibold text-gray-900 text-sm">{selectedLead.nome}</p>
                <p className="text-xs text-gray-500">{selectedLead.email}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Facilitador</label>
                <select value={form.facilitador_id} onChange={e => setForm({ ...form, facilitador_id: e.target.value })} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm">
                  <option value="">Selecione o facilitador</option>
                  {facilitadores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Data Início</label>
                  <input type="date" value={form.data_inicio} onChange={e => setForm({ ...form, data_inicio: e.target.value })} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Previsão Fim</label>
                  <input type="date" value={form.data_fim_prevista} onChange={e => setForm({ ...form, data_fim_prevista: e.target.value })} className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShow(false); setSelectedLead(null); }} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl font-semibold text-sm cursor-pointer">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-bold py-2.5 rounded-xl text-sm cursor-pointer">
                  {saving ? 'Salvando...' : 'Criar Inscrição'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}